import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDragLayer } from 'react-dnd';

import { funnelResultQuery2 } from '../../queries';
import { useSelectedSite, useStableValue, useTimeZonedFunctions } from '/src/hooks';
import { useFunnelEditorContext } from '../../funnel-editor-context';
import { DateRangeRow } from './DateRangeRow';
import { getEnumTimeUnit } from '../../utils';
//import { getFakeFunnelData } from '../../fake-data';
import { MainChart } from './MainChart';
import { TimeChart } from './TimeChart';
import { FunnelResultTable } from './FunnelResultTable';

export function FunnelResults({ stepsData, goals }) {
  const { date } = useFunnelEditorContext();
  const { selectedSite } = useSelectedSite();
  const { isDragging } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
  }));
  const stableStepsData = useStableValue(stepsData, isDragging);

  const { getConvertedDateRange } = useTimeZonedFunctions();
  const convertedDateRange = useMemo(() => getConvertedDateRange(date), [getConvertedDateRange, date]);
  const timeUnit = useMemo(() => getEnumTimeUnit(convertedDateRange), [convertedDateRange]);

  const { data, isFetching } = useQuery({
    ...funnelResultQuery2({
      siteId: selectedSite.id,
      startAt: convertedDateRange.startDate,
      endAt: convertedDateRange.endDate,
      timeUnit,
      steps: stableStepsData.map(({ id, type }) => ({ id, type })),
    }),
    enabled: !isDragging,
  });
  /*const data = useMemo(
    () =>
      getFakeFunnelData({
        steps: stableStepsData.map(({ id, type }) => ({ id, type })),
        dateRange: { startDate: convertedDateRange.startDate, endDate: convertedDateRange.endDate },
        timeUnit,
      }),
    [stableStepsData, timeUnit, convertedDateRange.startDate, convertedDateRange.endDate],
  );
  const isFetching = false;*/

  const summary = useMemo(() => data?.funnelResultNew?.summary ?? [], [data]);
  const totalValues = useMemo(() => data?.funnelResultNew?.values.map((v) => v.displayWorth) ?? [], [data]);
  const timeUnitResults = useMemo(() => data?.funnelResultNew?.timeUnitResults ?? [], [data]);

  // Currency supposed to be the same for the whole query, so we just take it once and use that
  const currency = useMemo(() => data?.funnelResultNew?.values?.[0]?.displayCurrency ?? 'USD', [data]);

  return (
    <section className="grow pl-5 pr-10 py-10 min-w-0">
      <DateRangeRow />
      <MainChart
        loading={isFetching}
        summary={summary}
        totalValues={totalValues}
        stepsData={stableStepsData}
        currency={currency}
      />
      <TimeChart
        loading={isFetching}
        stepsData={stableStepsData}
        timeUnit={timeUnit}
        timeUnitResults={timeUnitResults}
        currency={currency}
      />
      <FunnelResultTable
        loading={isFetching}
        summary={summary}
        totalValues={totalValues}
        stepsData={stableStepsData}
        goals={goals}
        currency={currency}
      />
    </section>
  );
}
